<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Check, ChevronDown, Loader2, Plus } from 'lucide-vue-next';

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | null | unknown;
    options: any[];
    valueProp?: string;
    labelProp?: string;
    placeholder?: string;
    searchable?: boolean;
    disabled?: boolean;
    loading?: boolean;
    noOptionsText?: string;
    actionLabel?: string;
    remoteSearch?: boolean;
    hasMore?: boolean;
    class?: any;
  }>(),
  {
    valueProp: 'id',
    labelProp: 'name',
    placeholder: 'Select...',
    searchable: true,
    disabled: false,
    loading: false,
    noOptionsText: 'No options found.',
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | null): void;
  (e: 'searchChange', query: string): void;
  (e: 'change', value: string | number | null): void;
  (e: 'action'): void;
  (e: 'loadMore'): void;
}>();

const handleAction = () => {
  isOpen.value = false;
  emit('action');
};

const isOpen = ref(false);
const searchQuery = ref('');

const selectedItem = computed(() => {
  if (props.modelValue == null) return null;
  // Support both raw ID values and object values (e.g. {id: 123, ...})
  const mv =
    props.valueProp && typeof props.modelValue === 'object' && props.modelValue !== null
      ? (props.modelValue as any)[props.valueProp]
      : props.modelValue;
  if (mv == null) return null;
  return props.options.find(opt => {
    const val = props.valueProp ? opt[props.valueProp] : opt;
    return val === mv || String(val) === String(mv);
  });
});

// Cache the label so it persists even if the selected item leaves the current options page
const cachedLabel = ref('');

watch(selectedItem, item => {
  if (item) {
    cachedLabel.value = props.labelProp ? item[props.labelProp] : String(item);
  }
});

const displayValue = computed(() => {
  if (selectedItem.value) {
    return props.labelProp ? selectedItem.value[props.labelProp] : String(selectedItem.value);
  }
  if (props.modelValue != null && cachedLabel.value) return cachedLabel.value;
  return '';
});

const handleSelect = (item: any) => {
  const value = props.valueProp ? item[props.valueProp] : item;
  const label = props.labelProp ? item[props.labelProp] : String(item);
  cachedLabel.value = label;
  emit('update:modelValue', value);
  emit('change', value);
  isOpen.value = false;
};

const handleSearchInput = (query: string) => {
  searchQuery.value = query;
  emit('searchChange', query);
};

const MAX_RENDERED_ITEMS = 100;

// Filter options locally when searchable, capped for performance
const allFilteredOptions = computed(() => {
  if (props.remoteSearch || !searchQuery.value) return props.options;
  const q = searchQuery.value.toLowerCase();
  return props.options.filter(opt => {
    const label = props.labelProp ? opt[props.labelProp] : String(opt);
    return String(label).toLowerCase().includes(q);
  });
});

const filteredOptions = computed(() => {
  const items = props.remoteSearch
    ? [...allFilteredOptions.value]
    : allFilteredOptions.value.slice(0, MAX_RENDERED_ITEMS);
  if (!selectedItem.value || !props.valueProp) return items;
  const selectedVal = selectedItem.value[props.valueProp];
  const idx = items.findIndex(opt => opt[props.valueProp!] === selectedVal);
  if (idx > 0) {
    const [item] = items.splice(idx, 1);
    items.unshift(item);
  } else if (idx === -1 && selectedItem.value) {
    // Selected item not in current page — prepend it
    items.unshift(selectedItem.value);
  }
  return items;
});
const hasMoreItems = computed(() => allFilteredOptions.value.length > MAX_RENDERED_ITEMS);

// Reset local search state when popover closes (remote options stay as-is)
watch(isOpen, open => {
  if (!open) {
    searchQuery.value = '';
  }
});
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <Button
        variant="secondary"
        role="combobox"
        :aria-expanded="isOpen"
        :disabled="disabled"
        :class="
          cn(
            'tw-w-full tw-justify-between tw-font-normal',
            !displayValue && 'tw-text-neutral-500',
            props.class
          )
        "
      >
        <span class="tw-truncate">{{ displayValue || placeholder }}</span>
        <Loader2
          v-if="loading"
          class="tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-animate-spin tw-opacity-50"
        />
        <ChevronDown v-else class="tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
      </Button>
    </PopoverTrigger>
    <PopoverContent class="tw-w-[--radix-popover-trigger-width] tw-p-0" align="start">
      <Command :disable-filter="remoteSearch">
        <CommandInput
          v-if="searchable"
          :placeholder="`${placeholder}...`"
          @update:model-value="handleSearchInput"
        />
        <CommandList>
          <!-- Action item (e.g. "Add New Supplier") -->
          <div
            v-if="actionLabel"
            class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-primary-500 tw-cursor-pointer hover:tw-bg-neutral-100 tw-border-b tw-border-neutral-100"
            @click="handleAction"
          >
            <Plus class="tw-h-4 tw-w-4" />
            {{ actionLabel }}
          </div>
          <CommandEmpty>{{ noOptionsText }}</CommandEmpty>
          <div
            v-if="loading && filteredOptions.length === 0"
            class="tw-flex tw-items-center tw-justify-center tw-py-6"
          >
            <Loader2 class="tw-h-5 tw-w-5 tw-animate-spin tw-text-neutral-400" />
          </div>
          <div
            v-else-if="!loading && filteredOptions.length === 0 && !searchQuery"
            class="tw-py-6 tw-text-center tw-text-sm tw-text-neutral-500"
          >
            {{ noOptionsText }}
          </div>
          <CommandGroup v-else>
            <CommandItem
              v-for="option in filteredOptions"
              :key="valueProp ? option[valueProp] : option"
              :value="labelProp ? option[labelProp] : String(option)"
              @select="handleSelect(option)"
            >
              <Check
                :class="
                  cn(
                    'tw-mr-2 tw-h-4 tw-w-4',
                    selectedItem &&
                      (valueProp ? selectedItem[valueProp] : selectedItem) ===
                        (valueProp ? option[valueProp] : option)
                      ? 'tw-opacity-100'
                      : 'tw-opacity-0'
                  )
                "
              />
              {{ labelProp ? option[labelProp] : option }}
            </CommandItem>
            <div
              v-if="hasMoreItems && !remoteSearch"
              class="tw-px-4 tw-py-2 tw-text-xs tw-text-neutral-400 tw-text-center"
            >
              Type to search for more results...
            </div>
          </CommandGroup>
          <div
            v-if="remoteSearch && hasMore"
            class="tw-flex tw-items-center tw-justify-center tw-px-4 tw-py-2 tw-border-t tw-border-neutral-100"
            @pointerdown.stop.prevent
          >
            <button
              type="button"
              class="tw-text-sm tw-text-primary-500 tw-font-medium hover:tw-text-primary-600 tw-flex tw-items-center tw-gap-1"
              :disabled="loading"
              @click.stop.prevent="emit('loadMore')"
            >
              <Loader2 v-if="loading" class="tw-h-3 tw-w-3 tw-animate-spin" />
              {{ loading ? '' : 'Load more...' }}
            </button>
          </div>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
